import type { Orientation } from '../../types'
import {
  FULL,
  fw,
  px,
  type BlueprintCaptions,
  type BlueprintItem,
  type BlueprintPicture,
  type BlueprintSolid,
  type BlueprintText,
  type BlueprintVisualizer,
  type StyleRole,
} from './types'

/** Shared pieces of the audio blueprints. */

/** A text's type: px size at the orientation's reference canvas, CSS weight, line height. */
export type Typo = {
  size: number
  weight: number
  lineHeight: number
  opacity?: number
}

type Box = [number, number, number, number]

/** The page: a full-canvas solid in the background role, under everything. */
export function background(): BlueprintSolid {
  return {
    id: 'background',
    type: 'solid',
    box: FULL,
    z: 0,
    color: 'background',
  }
}

/** The host picture (`clipScene.imageUrl`). */
export function host(
  o: Orientation,
  x: number,
  y: number,
  size: number,
  shape: BlueprintPicture['props']['shape'] = 'circle',
  extra: Partial<Pick<BlueprintPicture['props'], 'radius'>> = {},
): BlueprintPicture {
  return {
    id: 'host',
    type: 'component',
    componentId: 'picture',
    box: px(o, x, y, size, size),
    z: 20,
    props: { source: 'host', fit: 'cover', shape, ...extra },
  }
}

/**
 * The speaker's avatar, where v1 drew `speaker.avatarUrl`: whoever is
 * speaking on the media track, resolved at render time like the name.
 */
export function avatar(
  o: Orientation,
  x: number,
  y: number,
  size: number,
  shape: BlueprintPicture['props']['shape'] = 'circle',
): BlueprintPicture {
  return {
    id: 'avatar',
    type: 'component',
    componentId: 'picture',
    box: px(o, x, y, size, size),
    z: 21,
    props: { source: 'speaker', fit: 'cover', shape },
  }
}

/**
 * The speaker's name or handle: a dynamic text where v1 drew `speaker.*`,
 * at the type v1 set for it.
 */
export function speaker(
  o: Orientation,
  box: Box,
  field: 'name' | 'handle',
  typo: Typo,
  extra: Partial<Pick<BlueprintText, 'align' | 'surface' | 'color'>> = {},
): BlueprintText {
  return {
    id: field === 'name' ? 'speaker' : 'handle',
    type: 'text',
    text: field === 'name' ? 'speakerName' : 'speakerHandle',
    box: px(o, ...box),
    z: 22,
    color: 'speakerUsername',
    fontSize: fw(o, typo.size),
    fontWeight: typo.weight,
    lineHeight: typo.lineHeight,
    align: 'left',
    ...(typo.opacity !== undefined ? { opacity: typo.opacity } : {}),
    ...extra,
  }
}

/** The episode title or subtitle. A subtitle is at half opacity, as v1's style had it. */
export function text(
  o: Orientation,
  which: 'title' | 'subtitle',
  box: Box,
  typo: Typo,
  extra: Partial<Pick<BlueprintText, 'align' | 'fontStyle' | 'surface'>> = {},
): BlueprintText {
  const opacity = typo.opacity ?? (which === 'subtitle' ? 0.5 : undefined)
  return {
    id: which,
    type: 'text',
    text: which,
    box: px(o, ...box),
    z: which === 'title' ? 25 : 24,
    color: which,
    fontSize: fw(o, typo.size),
    fontWeight: typo.weight,
    lineHeight: typo.lineHeight,
    align: 'left',
    ...(opacity !== undefined ? { opacity } : {}),
    ...extra,
  }
}

/** A glyph drawn as text (the BigTitle quotes), in a role colour. */
export function glyph(
  o: Orientation,
  id: string,
  literal: string,
  box: Box,
  size: number,
  color: StyleRole,
): BlueprintText {
  return {
    id,
    type: 'text',
    text: 'literal',
    literal,
    box: px(o, ...box),
    z: 22,
    color,
    fontSize: fw(o, size),
    fontWeight: 700,
    lineHeight: 1,
    align: 'left',
  }
}

export function captions(
  o: Orientation,
  box: Box,
  align: NonNullable<BlueprintCaptions['captions']['align']> = 'center',
  {
    surface,
    lineHeight,
    linesPerPage,
  }: Partial<Pick<BlueprintCaptions, 'surface'>> &
    Pick<BlueprintCaptions['captions'], 'lineHeight' | 'linesPerPage'> = {},
): BlueprintCaptions {
  return {
    id: 'captions',
    type: 'captions',
    box: px(o, ...box),
    z: 40,
    captions: {
      typography: 'layout',
      align,
      ...(lineHeight !== undefined ? { lineHeight } : {}),
      ...(linesPerPage !== undefined ? { linesPerPage } : {}),
    },
    ...(surface !== undefined ? { surface } : {}),
  }
}

/** The audio visualizer, in the named look closest to the v1 layout's bars. */
export function visualizer(
  o: Orientation,
  box: Box,
  props: BlueprintVisualizer['props'] = { variant: 'bars' },
  extra: Partial<Pick<BlueprintVisualizer, 'surface'>> = {},
): BlueprintVisualizer {
  return {
    id: 'visualizer',
    type: 'component',
    componentId: 'audio-visualizer',
    box: px(o, ...box),
    z: 10,
    props,
    color: 'waveform',
    ...extra,
  }
}

/** A thin rule in a role colour. */
export function rule(
  o: Orientation,
  id: string,
  box: Box,
  color: StyleRole,
  opacity = 0.5,
): BlueprintSolid {
  return { id, type: 'solid', box: px(o, ...box), z: 5, color, opacity }
}

/** A card: a solid in a role colour that other items sit on. */
export function card(
  o: Orientation,
  id: string,
  box: Box,
  color: StyleRole = 'border',
  extra: Partial<Pick<BlueprintSolid, 'borderRadius' | 'opacity'>> = {},
): BlueprintSolid {
  return { id, type: 'solid', box: px(o, ...box), z: 2, color, ...extra }
}

/**
 * A frame: an outline in the border colour with nothing inside (the Chatbox
 * speech box, ScriptSmall's pill and episode card). `width` and `radius`
 * are in the orientation's reference pixels; v1 drew them at half opacity.
 */
export function frame(
  o: Orientation,
  id: string,
  box: Box,
  width: number,
  extra: { radius?: number; opacity?: number; color?: StyleRole } = {},
): BlueprintSolid {
  return {
    id,
    type: 'solid',
    box: px(o, ...box),
    z: 3,
    color: 'transparent',
    stroke: { color: extra.color ?? 'border', width: fw(o, width) },
    opacity: extra.opacity ?? 0.5,
    ...(extra.radius ? { borderRadius: fw(o, extra.radius) } : {}),
  }
}

export type Items = BlueprintItem[]
